import { onBeforeUnmount, onMounted, onUpdated, unref, watch, type MaybeRef } from 'vue'

interface InfiniteScrollOptions {
  canLoadUp?: MaybeRef<boolean>
  canLoadDown?: MaybeRef<boolean>
  loadUp?: () => void
  loadDown?: () => void
  direction: 'up' | 'down' | 'both'
  treshold?: number
  isDisabled?: MaybeRef<boolean>
  measure?: 'element-scroll' | 'client-rect'
}

export function useInfiniteScroll(targetEl: MaybeRef<HTMLElement | null>, options: InfiniteScrollOptions) {
  const abortController = new AbortController()

  const tryLoadMore = () => {
    if (unref(options.isDisabled)) return

    const targetElVal = unref(targetEl)
    if (!targetElVal) {
      return
    }

    const { treshold = 400, direction, canLoadUp, canLoadDown, loadDown, loadUp, measure = 'element-scroll' } = options

    let shouldLoadUp = false
    let shouldLoadDown = false

    if (direction === 'up' || direction === 'both') {
      if (measure === 'element-scroll') {
        shouldLoadUp = targetElVal.scrollTop <= treshold
      } else {
        const scrollTop = targetElVal.getBoundingClientRect().top
        shouldLoadUp = scrollTop <= treshold
      }
    }

    if (direction === 'down' || direction === 'both') {
      if (measure === 'element-scroll') {
        shouldLoadDown = targetElVal.scrollHeight - (targetElVal.scrollTop + targetElVal.clientHeight) <= treshold
      } else {
        const scrollBottom = targetElVal.getBoundingClientRect().bottom
        shouldLoadDown = scrollBottom - treshold <= window.innerHeight
      }
    }

    if (shouldLoadUp && unref(canLoadUp)) {
      loadUp?.()
      return
    }

    if (shouldLoadDown && unref(canLoadDown)) {
      loadDown?.()
    }
  }

  onMounted(() => {
    tryLoadMore()
    const { measure = 'element-scroll' } = options

    if (measure === 'element-scroll') {
      unref(targetEl)?.addEventListener('scroll', tryLoadMore, { signal: abortController.signal })
    } else {
      window.addEventListener('scroll', tryLoadMore, { signal: abortController.signal })
    }
  })

  watch(
    () => unref(targetEl),
    (targetEl, prevTargetEl) => {
      const { measure = 'element-scroll' } = options

      if (measure === 'element-scroll') {
        prevTargetEl?.removeEventListener('scroll', tryLoadMore)
        targetEl?.addEventListener('scroll', tryLoadMore, { signal: abortController.signal })
      }
      tryLoadMore()
    }
  )

  onUpdated(() => {
    tryLoadMore()
  })

  onBeforeUnmount(() => {
    abortController.abort()
  })
}
